import { useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  BillingTier,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  type DataTableToolbarProps,
  DataTableViewOptions,
  DateUI,
  getAccountName,
  Icon,
  InFlightTransfersHoverCard,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
} from "@repo/ui";
import { formatLocalDateComponents } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { formatDuration } from "date-fns";
import Decimal from "decimal.js";
import { createContext, Suspense, useContext } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import {
  RiBuilding4Line,
  RiFullscreenExitLine,
  RiFullscreenLine,
  RiWallet3Line,
} from "react-icons/ri";
import { useLocalStorage } from "usehooks-ts";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { EntityLink } from "~/components/link/EntityLink/EntityLink";
import { graphql } from "~/gql/client";
import type { AdminOrgListOrgFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/organisations")({
  head: () => opsHead("Organisations"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminOrgList {
        organisations {
          ...AdminOrgListOrg
        }
      }

      fragment AdminOrgListOrg on Organisation {
        id
        name
        billingTier
        assetCount
        homeCurrency
        squareLogoUrl
        fixedMonthlyFeeContractEndDate
        businessEntities {
          id
        }
        estimatedValue {
          currency
          amount
        }
        consistentBalance {
          asOf
          balance {
            currency
            amount
          }
          assetsByEffectiveAt {
            id
            approxEffectiveAt
            assets {
              id
            }
          }
        }
        consistentBalanceChangedAssets: assets(
          filter: { consistentBalanceChange: true }
        ) {
          ...AssetCardFields
          sourceDataAsOf
          consistentBalance {
            id
            change
            balance {
              currency
            }
          }
        }
        monthlyServiceFeeEstimate {
          blendedServiceFeeRateBps
          feeEstimate {
            currency
            amount
          }
        }
        inflightTotal {
          currency
          amount
        }
        inflightTransfers {
          id
          unitAmount {
            amount
          }
          sourceAsset {
            id
            name
            providerName
            currency
            logo {
              id
              url
            }
          }
          destinationAccount {
            ... on Asset {
              id
              name
              providerName
              logo {
                id
                url
              }
            }
            ... on BeneficiaryAccount {
              id
              displayName
              bankName
              logoUrl
            }
          }
        }
        latestMonthlyStatement {
          id
          licenseFeeExGst {
            currency
            amount
          }
        }
      }
    `),
  );

  const [isFullWidth, setIsFullWidth] = useLocalStorage(
    "ops-organisations-full-width",
    false,
  );
  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>("ops-organisations-column-visibility", {});

  return (
    <AdminPageContainer testId="admin-organisations" fullWidth={isFullWidth}>
      <AdminPageHeader
        title="Organisations"
        subtitle="All organisations in the Primary database"
        rightContent={
          <Button asChild variant="outline">
            <Link to="/organisations/balance-summaries">
              View balance summaries
            </Link>
          </Button>
        }
      />
      <div>
        {(data.organisations ?? []).length === 0 ? (
          <div>No organisations</div>
        ) : (
          <OrganisationsTableToolbarContext.Provider
            value={{
              isFullWidth,
              onToggleFullWidth: () => setIsFullWidth((prev) => !prev),
            }}
          >
            <DataTable
              data={data.organisations ?? []}
              columns={columns}
              linkTo={(row) => `/organisations/${row.original.id}`}
              columnSortProps={{
                id: "name",
                desc: false,
              }}
              pageSize={100_000}
              className="overflow-hidden border"
              hidePagination
              columnVisibility={{
                consistentBalance: false,
                consistentBalanceAsOf: false,
                consistentBalanceTimeRange: false,
                ...columnVisibility,
              }}
              onColumnVisibilityChange={setColumnVisibility}
              toolbarComponent={OrganisationsTableToolbar}
            />
          </OrganisationsTableToolbarContext.Provider>
        )}
      </div>
    </AdminPageContainer>
  );
}

// DataTable renders toolbarComponent as a component, so it must be a stable
// module-level reference. Props are passed via context instead.
const OrganisationsTableToolbarContext = createContext<
  | {
      isFullWidth: boolean;
      onToggleFullWidth: () => void;
    }
  | undefined
>(undefined);

function OrganisationsTableToolbar({
  table,
}: DataTableToolbarProps<AdminOrgListOrgFragment>) {
  const toolbarContext = useContext(OrganisationsTableToolbarContext);
  if (!toolbarContext) {
    throw new Error(
      "OrganisationsTableToolbar requires OrganisationsTableToolbarContext",
    );
  }
  const { isFullWidth, onToggleFullWidth } = toolbarContext;
  return (
    <div className="flex flex-row justify-end gap-2">
      <DataTableViewOptions table={table} />
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        leftIcon={isFullWidth ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
        onClick={onToggleFullWidth}
      >
        {isFullWidth ? "Exit fullscreen" : "Fullscreen"}
      </Button>
    </div>
  );
}

const columns: ColumnDef<AdminOrgListOrgFragment>[] = [
  {
    id: "logo",
    cell: ({ row }) => (
      <Icon
        size="8"
        imageUrl={row.original.squareLogoUrl ?? undefined}
        fallbackIcon={<PiBuildingOffice />}
      />
    ),
  },
  {
    id: "name",
    accessorFn: (row) => row.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <Txt variant="name">{row.original.name ?? "-"}</Txt>,
    sortingFn: (a, b) =>
      (a.original.name ?? "").localeCompare(b.original.name ?? ""),
  },
  {
    id: "billingTier",
    accessorFn: (row) => row.billingTier,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tier" />
    ),
    cell: ({ row }) => (
      <BillingTier tier={row.original.billingTier} className="h-8 w-8" />
    ),
  },
  {
    id: "businessEntities",
    accessorFn: (row) => row.businessEntities?.length ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Entities" />
    ),
    cell: ({ row }) => (
      <div className="flex gap-4 font-semibold">
        <EntityLink to={`/organisations/${row.original.id}/business-entities`}>
          <div className="flex items-center gap-1">
            {row.original.businessEntities?.length ?? 0}{" "}
            <RiBuilding4Line size={16} />
          </div>
        </EntityLink>
        <EntityLink to={`/assets?organisationId=${row.original.id}`}>
          <div className="flex items-center gap-1">
            {row.original.assetCount} <RiWallet3Line size={16} />
          </div>
        </EntityLink>
      </div>
    ),
    sortingFn: (a, b) =>
      (a.original.businessEntities?.length ?? 0) -
      (b.original.businessEntities?.length ?? 0),
  },
  {
    id: "inFlightNet",
    accessorFn: (row) => row.inflightTotal?.amount ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="In Flight" align="right" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          {row.original?.inflightTransfers &&
          row.original.inflightTransfers.length > 0 ? (
            <InFlightTransfersHoverCard
              inFlightTransfers={
                row.original.inflightTransfers.flatMap((t) => {
                  if (
                    !t.unitAmount ||
                    !t.destinationAccount ||
                    !t.sourceAsset ||
                    !t.sourceAsset.currency
                  ) {
                    return [];
                  }
                  return [
                    {
                      amount: {
                        amount: t.unitAmount.amount,
                        currency: t.sourceAsset.currency,
                      },
                      source: t.sourceAsset.name,
                      sourceProviderName: t.sourceAsset.providerName ?? "",
                      destination: getAccountName(t.destinationAccount) ?? "",
                      destinationProviderName:
                        (t.destinationAccount?.__typename ===
                        "BeneficiaryAccount"
                          ? t.destinationAccount.bankName
                          : t.destinationAccount?.providerName) ?? "",
                      sourceProviderLogo: t.sourceAsset?.logo?.url ?? undefined,
                      destinationProviderLogo:
                        t.destinationAccount?.__typename ===
                        "BeneficiaryAccount"
                          ? (t.destinationAccount.logoUrl ?? undefined)
                          : (t.destinationAccount?.logo?.url ?? undefined),
                    },
                  ];
                }) ?? []
              }
            >
              <CurrencyAmountUI
                amount={row.original.inflightTotal?.amount ?? 0}
                currency={
                  row.original.inflightTotal?.currency ??
                  row.original.homeCurrency
                }
              />
            </InFlightTransfersHoverCard>
          ) : (
            <CurrencyAmountUI
              amount={row.original.inflightTotal?.amount ?? 0}
              currency={
                row.original.inflightTotal?.currency ??
                row.original.homeCurrency
              }
            />
          )}
        </div>
      );
    },
    sortingFn: (a, b) =>
      (a.original.inflightTotal?.amount ?? new Decimal(0)).comparedTo(
        b.original.inflightTotal?.amount ?? new Decimal(0),
      ),
  },
  {
    id: "licenseFeeRenewalDate",
    accessorFn: (row) => row.fixedMonthlyFeeContractEndDate ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Renewal date"
        align="left"
      />
    ),
    cell: ({ row }) => {
      return row.original.fixedMonthlyFeeContractEndDate ? (
        <div className="flex flex-col items-end">
          <DateUI
            {...{
              date: row.original.fixedMonthlyFeeContractEndDate,
              variant: "DATE",
            }}
          />
        </div>
      ) : (
        "-"
      );
    },
    sortingFn: (a, b) =>
      (a.original.fixedMonthlyFeeContractEndDate ?? new Date()).getTime() -
      (b.original.fixedMonthlyFeeContractEndDate ?? new Date()).getTime(),
  },
  {
    id: "licenseFee",
    accessorFn: (row) =>
      row.latestMonthlyStatement?.licenseFeeExGst ?? new Decimal(0),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="License fee"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return row.original.latestMonthlyStatement?.licenseFeeExGst ? (
        <div className="flex flex-col items-end">
          <CurrencyAmountUI
            {...row.original.latestMonthlyStatement.licenseFeeExGst}
          />
        </div>
      ) : (
        "-"
      );
    },
    sortingFn: (a, b) =>
      (
        a.original.latestMonthlyStatement?.licenseFeeExGst?.amount ??
        new Decimal(0)
      ).comparedTo(
        b.original.latestMonthlyStatement?.licenseFeeExGst?.amount ??
          new Decimal(0),
      ),
  },
  {
    id: "blendedServiceFeeRate",
    accessorFn: (row) =>
      row.monthlyServiceFeeEstimate?.blendedServiceFeeRateBps?.toNumber() ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Blended Fee (bps)"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          <Tooltip delayDuration={250}>
            <TooltipTrigger>
              {row.original.monthlyServiceFeeEstimate?.blendedServiceFeeRateBps
                ?.toDP(2)
                .toNumber() ?? 0}
            </TooltipTrigger>
            <TooltipContent>
              Monthly estimate:{" "}
              <CurrencyAmountUI
                amount={
                  row.original.monthlyServiceFeeEstimate?.feeEstimate?.amount ??
                  0
                }
                currency={
                  row.original.monthlyServiceFeeEstimate?.feeEstimate
                    ?.currency ?? "AUD"
                }
                hideCurrency
              />
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  {
    id: "totalBalance",
    accessorFn: (row) => row.estimatedValue?.amount ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Total Balance"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          <CurrencyAmountUI
            amount={row.original.estimatedValue?.amount ?? 0}
            currency={
              row.original.estimatedValue?.currency ?? row.original.homeCurrency
            }
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      (a.original.estimatedValue?.amount ?? new Decimal(0)).comparedTo(
        b.original.estimatedValue?.amount ?? new Decimal(0),
      ),
  },
  {
    id: "consistentBalance",
    accessorFn: (row) => row.consistentBalance?.balance.amount ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Consistent Balance"
        align="right"
      />
    ),
    cell: ({ row }) => {
      if (!row.original.consistentBalance) {
        return "-";
      }
      const changedAssets = [
        ...(row.original.consistentBalanceChangedAssets ?? []),
      ].sort(
        (a, b) =>
          (a.sourceDataAsOf?.getTime() ?? Number.POSITIVE_INFINITY) -
          (b.sourceDataAsOf?.getTime() ?? Number.POSITIVE_INFINITY),
      );
      const asOf = formatLocalDateComponents(
        row.original.consistentBalance.asOf,
      ).datetime;
      return (
        <div className="flex flex-col items-end">
          <Tooltip delayDuration={250}>
            <TooltipTrigger>
              <CurrencyAmountUI
                ca={row.original.consistentBalance.balance}
                nonCopyable
              />
            </TooltipTrigger>
            <TooltipContent className="rounded-lg border bg-white p-2 text-left text-primary shadow-surface">
              {changedAssets.length === 0 ? (
                <Txt variant="value">No further movements since {asOf}</Txt>
              ) : (
                <div className="flex flex-col gap-2">
                  <Txt variant="value">Account movements since {asOf}</Txt>
                  <div className="grid grid-cols-[auto_auto_auto]">
                    {changedAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="col-span-3 grid grid-cols-subgrid items-center gap-x-4 rounded-md px-2 py-1 odd:bg-white even:bg-neutral-muted"
                      >
                        <div className="flex items-center gap-2">
                          <AccountCard
                            variant="INLINE_TEXT"
                            subText="MASKED_IDENTIFIER"
                            account={asset}
                          />
                        </div>
                        <Txt variant="description">
                          {asset.sourceDataAsOf
                            ? formatLocalDateComponents(asset.sourceDataAsOf)
                                .datetime
                            : "-"}
                        </Txt>
                        <div className="justify-self-end">
                          {asset.consistentBalance ? (
                            <CurrencyAmountUI
                              amount={asset.consistentBalance.change}
                              currency={
                                asset.consistentBalance.balance.currency
                              }
                              showSign
                              nonCopyable
                            />
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
    sortingFn: (a, b) =>
      (
        a.original.consistentBalance?.balance.amount ?? new Decimal(0)
      ).comparedTo(
        b.original.consistentBalance?.balance.amount ?? new Decimal(0),
      ),
  },
  {
    id: "consistentBalanceAsOf",
    accessorFn: (row) => row.consistentBalance?.asOf ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consistent Balance As Of" />
    ),
    cell: ({ row }) => {
      if (!row.original.consistentBalance) {
        return "-";
      }
      const groups = row.original.consistentBalance.assetsByEffectiveAt ?? [];
      const groupedAssetCount = groups.reduce(
        (sum, group) => sum + (group.assets?.length ?? 0),
        0,
      );
      const unknownCount = (row.original.assetCount ?? 0) - groupedAssetCount;
      return (
        <Tooltip delayDuration={250}>
          <TooltipTrigger>
            {
              formatLocalDateComponents(row.original.consistentBalance.asOf)
                .datetime
            }
          </TooltipTrigger>
          <TooltipContent className="rounded-lg border bg-white p-2 text-left text-primary shadow-surface">
            {groups.length === 0 ? (
              <Txt variant="value">No known account balance times</Txt>
            ) : (
              <div className="flex flex-col gap-1">
                <Txt variant="value">Account balances effective at</Txt>
                {groups.map((group) => {
                  const count = group.assets?.length ?? 0;
                  return (
                    <Txt key={group.id} variant="description">
                      {
                        formatLocalDateComponents(group.approxEffectiveAt)
                          .datetime
                      }{" "}
                      - {count} {count === 1 ? "account" : "accounts"}
                    </Txt>
                  );
                })}
                {unknownCount > 0 ? (
                  <Txt variant="description">
                    Unknown - {unknownCount}{" "}
                    {unknownCount === 1 ? "account" : "accounts"}
                  </Txt>
                ) : null}
              </div>
            )}
          </TooltipContent>
        </Tooltip>
      );
    },
    sortingFn: (a, b) =>
      (a.original.consistentBalance?.asOf?.getTime() ?? 0) -
      (b.original.consistentBalance?.asOf?.getTime() ?? 0),
  },
  {
    id: "consistentBalanceTimeRange",
    accessorFn: (row) => {
      const groups = row.consistentBalance?.assetsByEffectiveAt ?? [];
      const earliest = groups.at(0);
      const latest = groups.at(-1);
      if (!earliest || !latest) {
        return null;
      }
      return (
        latest.approxEffectiveAt.getTime() -
        earliest.approxEffectiveAt.getTime()
      );
    },
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Consistent Balance Time Range"
      />
    ),
    cell: ({ getValue }) => {
      const rangeMs = getValue<number | null>();
      if (rangeMs === null) {
        return "-";
      }
      const totalMinutes = Math.round(rangeMs / 60_000);
      return (
        formatDuration({
          days: Math.floor(totalMinutes / (24 * 60)),
          hours: Math.floor((totalMinutes % (24 * 60)) / 60),
          minutes: totalMinutes % 60,
        }) || "0 minutes"
      );
    },
    sortingFn: (a, b) =>
      (a.getValue<number | null>("consistentBalanceTimeRange") ?? -1) -
      (b.getValue<number | null>("consistentBalanceTimeRange") ?? -1),
  },
];
